import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui-kit/command';
import { cn } from '@/lib/utils';
import { InlineSkeleton, OptionRowsSkeleton } from '@/components/core';
import { useGetEmployees } from '../../hooks/use-projects';
import { EmployeeOption, EmployeesResult } from '../../types/project.types';

export interface EmployeeMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  excludeIds?: string[];
  /** Called with the display names of the currently selected employees, in `value` order. */
  onSelectedNamesChange?: (names: string[]) => void;
}

export const EmployeeMultiSelect = ({
  value,
  onChange,
  placeholder,
  excludeIds = [],
  onSelectedNamesChange,
}: EmployeeMultiSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  // Accumulate every employee option ever fetched so previously selected
  // employees keep showing their name even after the search keyword changes.
  const namesCacheRef = useRef<Map<string, string>>(new Map());

  const { data, isLoading } = useGetEmployees(keyword);
  const fetchedOptions = useMemo(
    () => (data as { Employees?: EmployeesResult })?.Employees?.items ?? [],
    [data]
  );

  useEffect(() => {
    fetchedOptions.forEach((option) =>
      namesCacheRef.current.set(option.ItemId, option.DisplayName)
    );
  }, [fetchedOptions]);

  useEffect(() => {
    if (!onSelectedNamesChange) return;
    onSelectedNamesChange(value.map((id) => namesCacheRef.current.get(id) ?? id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, fetchedOptions]);

  const options = fetchedOptions.filter((option) => !excludeIds.includes(option.ItemId));
  const selectedOptions: EmployeeOption[] = value.map(
    (id) =>
      options.find((option) => option.ItemId === id) ?? {
        ItemId: id,
        DisplayName: namesCacheRef.current.get(id) ?? id,
      }
  );

  const toggle = (itemId: string) => {
    onChange(value.includes(itemId) ? value.filter((id) => id !== itemId) : [...value, itemId]);
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* modal: keeps mouse-wheel / touch scrolling working inside the list when this
          popover is opened from within a Dialog (whose scroll lock would otherwise block it). */}
      <Popover
        modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setKeyword('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'h-9 w-full min-w-0 justify-between px-3 font-normal',
              value.length === 0 && 'text-muted-foreground'
            )}
          >
            <span className="truncate">
              {value.length > 0 ? `${value.length} ${t('SELECTED')}` : placeholder}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {/* Same width as the trigger; the primitives cap it to the viewport and available height. */}
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput placeholder={placeholder} value={keyword} onValueChange={setKeyword} />
            <CommandList>
              {isLoading && options.length === 0 ? (
                <OptionRowsSkeleton />
              ) : (
                <CommandEmpty>{t('NO_DATA_FOUND')}</CommandEmpty>
              )}
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.ItemId}
                    value={option.ItemId}
                    onSelect={() => toggle(option.ItemId)}
                    className="min-h-10 sm:min-h-0"
                  >
                    <Checkbox
                      checked={value.includes(option.ItemId)}
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none"
                    />
                    <span className="truncate">{option.DisplayName}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedOptions.length > 0 && (
        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
          {selectedOptions.map((option) => (
            <Badge key={option.ItemId} variant="outline" className="max-w-full gap-1">
              {/* A saved employee whose name hasn't loaded yet: a placeholder, not their raw id. */}
              {isLoading && option.DisplayName === option.ItemId ? (
                <InlineSkeleton className="h-3 w-20" />
              ) : (
                <span className="truncate">{option.DisplayName}</span>
              )}
              <button
                type="button"
                className="shrink-0"
                onClick={() => toggle(option.ItemId)}
                aria-label={`Remove ${option.DisplayName}`}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
